import type { EdgeInfo } from '../pieces/JigsawEdgeGrid';
import { getEdgeProfile } from '../pieces/EdgeProfiles';

export type EdgeOrientation = 'top' | 'right' | 'bottom' | 'left';

/** Нормализованные координаты кусочка 0..1 (левый верх = 0,0). */
export type PointNorm = { x: number; y: number };

const SEGMENTS_SAMPLES = 12;

/**
 * Сэмплирует одну кубическую кривую Безье и возвращает точки.
 */
function sampleBezier(
  p0: PointNorm,
  p1: PointNorm,
  p2: PointNorm,
  p3: PointNorm,
  numSamples: number
): PointNorm[] {
  const out: PointNorm[] = [];
  for (let i = 0; i <= numSamples; i++) {
    const t = i / numSamples;
    const u = 1 - t;
    const x = u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x;
    const y = u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y;
    out.push({ x, y });
  }
  return out;
}

/**
 * Возвращает точки пути края в нормализованных координатах кусочка (0..1).
 * origin у кусочка в игре — центр, поэтому в мир: worldX = originX + (x - 0.5) * pieceW.
 */
export function getEdgePathPointsNorm(
  edge: EdgeInfo,
  orientation: EdgeOrientation,
  tabRatio: number
): PointNorm[] {
  if (edge.type === 'flat') {
    const [from, to] = edgeEndpointsNorm(orientation);
    return [from, to];
  }

  const profile = getEdgeProfile(edge.variant);
  if (!profile?.a?.length && !profile?.b?.length) {
    const [from, to] = edgeEndpointsNorm(orientation);
    return [from, to];
  }

  const [from, to] = edgeEndpointsNorm(orientation);
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.max(1e-9, Math.hypot(dx, dy));
  const tx = dx / len;
  const ty = dy / len;
  const edgeSign = edge.type === 'in' ? -1 : 1;
  const tab = Math.max(0, Math.min(1, tabRatio));

  const normal = normalForOrientation(orientation);

  const mapPoint = (u: number, v: number): PointNorm => {
    const baseX = from.x + tx * (u * len);
    const baseY = from.y + ty * (u * len);
    const offset = (v - 0.5) * 2 * tab * edgeSign;
    return {
      x: baseX + normal.x * offset,
      y: baseY + normal.y * offset,
    };
  };

  const segs = [...profile.a, ...profile.b];
  const points: PointNorm[] = [from];
  let prev = from;

  for (const seg of segs) {
    const c1 = mapPoint(seg.cx1, seg.cy1);
    const c2 = mapPoint(seg.cx2, seg.cy2);
    const p = mapPoint(seg.x, seg.y);
    const sampled = sampleBezier(prev, c1, c2, p, SEGMENTS_SAMPLES);
    for (let i = 1; i < sampled.length; i++) {
      points.push(sampled[i]);
    }
    prev = p;
  }

  points.push(to);
  return points;
}

function edgeEndpointsNorm(orientation: EdgeOrientation): [PointNorm, PointNorm] {
  switch (orientation) {
    case 'top':
      return [{ x: 0, y: 0 }, { x: 1, y: 0 }];
    case 'right':
      return [{ x: 1, y: 0 }, { x: 1, y: 1 }];
    case 'bottom':
      return [{ x: 1, y: 1 }, { x: 0, y: 1 }];
    case 'left':
      return [{ x: 0, y: 1 }, { x: 0, y: 0 }];
  }
}

function normalForOrientation(orientation: EdgeOrientation): PointNorm {
  switch (orientation) {
    case 'top':
      return { x: 0, y: -1 };
    case 'right':
      return { x: 1, y: 0 };
    case 'bottom':
      return { x: 0, y: 1 };
    case 'left':
      return { x: -1, y: 0 };
  }
}

/**
 * Переводит нормализованные точки (0..1, origin = левый верх) в мировые координаты.
 * В игре origin кусочка — центр, displaySize = pieceW x pieceH.
 */
export function normToWorld(
  points: PointNorm[],
  originWorldX: number,
  originWorldY: number,
  pieceW: number,
  pieceH: number
): { x: number; y: number }[] {
  return points.map((p) => ({
    x: originWorldX + (p.x - 0.5) * pieceW,
    y: originWorldY + (p.y - 0.5) * pieceH,
  }));
}
